import React, { useState, useCallback, useEffect } from "react";
import { GeocodeResponse } from "../../../types/geocode";
import { searchAddress } from "../../../api/geocode";
import useDebouncer from "../../../hooks/debouncer";
import LoadingSpinner from "../../ui/LoadingSpinner";

type AddressSearchProps = {
    label?: string;
    onSelect: (address: GeocodeResponse) => void;
};

export const AddressSearch: React.FC<AddressSearchProps> = ({
    label = "Search for an address",
    onSelect,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState<GeocodeResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const debouncedSearchQuery = useDebouncer(searchQuery, 500);

    useEffect(() => {
        const performSearch = async () => {
            if (!debouncedSearchQuery || debouncedSearchQuery.trim() === "") {
                setResults([]);
                setHasSearched(false);
                return;
            }

            setLoading(true);
            setHasSearched(true);
            try {
                const searchResults = await searchAddress(debouncedSearchQuery);
                setResults(searchResults);
            } catch (error) {
                console.error("Error searching address:", error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [debouncedSearchQuery]);

    const handleSelect = useCallback((address: GeocodeResponse) => {
        onSelect(address);
        setSearchQuery("");
        setResults([]);
        setHasSearched(false);
    }, [onSelect]);

    return (
        <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{label}</label>
            <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type to search for an address..."
                className="h-10 w-full shadow-md rounded-md px-4 outline-none"
            />

            {loading && (
                <div className="flex items-center gap-2 text-sm">
                    <LoadingSpinner height={16} width={16} color="#716F6F" />
                    <span>Searching...</span>
                </div>
            )}

            {!loading && hasSearched && results.length === 0 && (
                <div className="text-sm py-2">
                    No addresses found. Try a different search.
                </div>
            )}

            {!loading && results.length > 0 && (
                <div className="flex flex-col gap-2 max-h-64 overflow-y-auto border-light-grey border rounded-md p-2">
                    {results.map((result, index) => (
                        <label
                            key={`${result.latitude}-${result.longitude}-${index}`}
                            className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                            <input
                                type="radio"
                                name="address-selection"
                                className="mt-1 cursor-pointer"
                                onChange={() => handleSelect(result)}
                            />
                            <span className="text-sm flex-1">{result.formatted}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};
